import { create } from "zustand";
import {
  IJet,
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { syncTabs } from "zustand-sync-tabs";
import { devtools } from "zustand/middleware";
import { IdleManager } from "tools/idle";
import { registerStore } from "store/utils/reset-store.utils";

// Per show, so one show's fetch never blocks another's.
const inFlightShowIds = new Set<string>();

type State = {
  items: Record<string, IJet | null>;
  itemsLoaded: Record<string, boolean>;
  isLoading: boolean;
};

type Action = {
  fetch: (showId: string, force?: boolean) => Promise<void>;
  update: (showId: string) => Promise<void>;
  autoCatch: (showId: string) => Promise<void>;
  getByShowId: (showId: string) => IJet | null;
  getAll: () => Record<string, IJet | null>;
};

export const useJetStore = create(
  devtools<State & Action>(
    syncTabs(
      (set, get) => ({
        items: {},
        itemsLoaded: {},
        isLoading: false,

        getAll: () => {
          const { items } = get();

          return items;
        },

        getByShowId: (showId: string): IJet | null => {
          const { items } = get();

          if (!items?.[showId]) {
            return null;
          }

          return items[showId];
        },

        autoCatch: async (showId: string) => {
          const { update } = get();

          IdleManager.getInstance().addSubscriber(`jet-${showId}`, async () => {
            await update(showId);
          });

          apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
            key: NotificationType.UPDATED_JET_PLATFORM,
            showId,
            callback: async ({ showId }) => {
              await update(showId);
            },
          });
        },

        update: async (showId: string) => {
          try {
            const jet = await apiV2.jet
              .get({
                showId,
              })
              .catch(() => {
                return null;
              });

            set((state) => ({
              items: {
                ...state.items,

                [showId]: jet,
              },
            }));
          } catch (e) {
            console.error(e);
          } finally {
            set((state) => ({
              itemsLoaded: { ...state.itemsLoaded, [showId]: true },
            }));
          }
        },
        fetch: async (showId: string, force = false) => {
          const { update } = get();

          if (!force) {
            if (get().itemsLoaded[showId] || inFlightShowIds.has(showId)) {
              return;
            }
          }

          inFlightShowIds.add(showId);
          set(() => ({ isLoading: true }));

          try {
            await update(showId);
          } catch (e) {
            console.error("jet store:", e);
          } finally {
            inFlightShowIds.delete(showId);
            set(() => ({
              isLoading: inFlightShowIds.size > 0,
            }));
          }
        },
      }),
      {
        name: "show-jet",
      }
    )
  )
);

registerStore(useJetStore);
